import {
    useInfinitePagination,
    useResources,
    useRouteMatch,
    useRouter,
} from '@funtech-int/rival-react';
import { Selection, selectionQuery } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { createInfiniteQuery } from '@funtech-int/rival';
import { useAuthManager } from '@funtech-int/yango-shared';

import { useSearchParams } from '@/shared/hooks/useSearchParams';
import { GraphqlClient } from '@/shared/lib/graphqlClient';
import { useGraphqlClient } from '@/shared/hooks/useGraphqlClient';
import { useInMemoryCache } from '@/shared/hooks/useInMemoryCache';
import { InMemoryCache } from '@/shared/lib/inMemoryCache';
import { getMovieUGCKey } from '@/shared/lib/movieUGC';
import { NotFoundAppError } from '@/shared/lib/errors/NotFoundAppError';
import { EmptySelectionError } from '@/shared/lib/errors/EmptySelectionError';

import { selectionRoute } from '../routes';

const SELECTION_LIMIT = 25;

interface SelectionParams {
    id: string;
    showcaseId: string | null;
    withUserData: boolean;
    withRating: boolean;
}

interface SelectionQueryContext {
    client: GraphqlClient;
    inMemoryCache: InMemoryCache;
    params: SelectionParams;
}

interface SelectionPageInfo {
    offset: number;
}

const selectionResourceQuery = createInfiniteQuery<
    SelectionQueryContext,
    Selection | null | undefined,
    SelectionPageInfo
>({
    key(ctx) {
        return [selectionQuery.name, ctx.params.id, ctx.params.showcaseId]
            .filter(Boolean)
            .join(':');
    },
    fetch(ctx, prev, pageInfo) {
        const offset = pageInfo?.offset ?? 0;

        return ctx.client
            .fetch({
                operation: selectionQuery,
                variables: {
                    id: ctx.params.id,
                    showcaseId: ctx.params.showcaseId,
                    withUserData: ctx.params.withUserData,
                    selectionOffset: offset,
                    selectionLimit: SELECTION_LIMIT,
                    withRating: ctx.params.withRating,
                },
            })
            .then(({ data }) => {
                if (!data) {
                    return prev ?? null;
                }

                data.items.forEach((item) => {
                    ctx.inMemoryCache.set(
                        getMovieUGCKey(item.id, 'plannedToWatch'),
                        Boolean(item.userData?.isPlannedToWatch)
                    );
                });

                const prevItems = prev?.items ?? [];

                return {
                    ...data,
                    items: prevItems.concat(data.items),
                };
            });
    },
    nextPageInfo(data) {
        if (data?.hasMore) {
            return {
                offset: data.offset + SELECTION_LIMIT,
            };
        }
    },
    toJSON(data: any) {
        return data;
    },
    hydrate(data: any) {
        return selectionQuery.hydrate(data) as any;
    },
});

export function useSelectionQuery() {
    const client = useGraphqlClient();
    const inMemoryCache = useInMemoryCache();
    const router = useRouter();

    const location = useRouteMatch(selectionRoute);
    const selectionId = location.params.id;
    const searchParams = useSearchParams();
    const showcaseId = searchParams.get('selectionWindowId') ?? null;

    const { isAuthorized } = useAuthManager();

    const isWatchLaterSelection = selectionId === 'watch_later_any';

    const pagination = useInfinitePagination();

    const [selection] = useResources(
        selectionResourceQuery(
            {
                params: {
                    id: selectionId,
                    showcaseId,
                    withUserData: isAuthorized,
                    withRating: false,
                },
                client,
                inMemoryCache,
            },
            { pagination }
        )
    );

    const selectionKey = [selectionId, showcaseId].filter(Boolean).join('/');

    if (!selection || selection.items.length === 0) {
        if (isWatchLaterSelection) {
            throw new EmptySelectionError(selectionKey, selection?.title);
        }
        throw new NotFoundAppError(router.serialize(location), {
            message: 'Selection not found',
            context: { selectionKey },
        });
    }

    return { selection, isWatchLaterSelection, pagination };
}
